import React, { useEffect } from "react";
import { Box } from "@mui/material";

import { Loading } from "components";

import {
  DataBoxesList,
  MissingDocumentsExportButton,
  MissingDocumentsFiltersBar,
  MissingDocumentsTable,
} from "./components";

import {
  useMissingDocumentsFilters,
  useMissingDocumentsReferenceData,
  useMissingDocumentsTable,
} from "./hooks";

export const MissingDocumentsPage = () => {
  const {
    availableYears,
    companyMonths,
    hasCompanyFiscalConfig,
    allFiscalYears,
    countries,
    utilityTypes,
    sites,
    businessUnits,
    countryRegions,
    sitesByCountry,
    allMonths,
  } = useMissingDocumentsReferenceData();

  const {
    defaultObject,
    currentFY,
    setCurrentFY,
    onlyMissing,
    setOnlyMissing,
    searchActive,
    setSearchActive,
    enableSearch,
    areSitesFiltered,
    currentCountries,
    currentSiteIds,
    currentUtilityTypeIds,
    currentBusinessUnits,
    currentCountryRegions,
    currentStatus,
    getSitesFiltered,
    resetFilterState,
    allStatusOptions,
    countrySetSelectedValues,
    countryOnChange,
    utilityTypeSetSelectedValues,
    utilityTypeOnChange,
    sitesSetSelectedValues,
    sitesOnChange,
    businessUnitSetSelectedValues,
    businessUnitOnChange,
    regionSetSelectedValues,
    regionOnChange,
    statusSetSelectedValues,
    statusOnChange,
  } = useMissingDocumentsFilters({
    sites,
    companyMonths,
    hasCompanyFiscalConfig,
    sitesByCountry,
    countries,
    utilityTypes,
    businessUnits,
    countryRegions,
  });

  const {
    pagination,
    currentMissingData,
    allCurrentCollectorsIds,
    loadingMissingDocuments,
    isTableRequestInProgress,
    columns,
    columnFilters,
    setColumnFilters,
    globalFilter,
    setGlobalFilter,
    uiPage,
    handlePageChange,
    handleSizeChange,
    totalSelectedRows,
    allRowsSelected,
    selectedRows,
    openMonthView,
    setOpenMonthView,
    selectedMonthCell,
    collectorMissingDocuments,
    handleUpdateMissingDocuments,
    resetSelection,
    resetPage,
  } = useMissingDocumentsTable({
    defaultObject,
    currentFY,
    companyMonths,
    allMonths,
    onFetchComplete: () => setSearchActive(false),
  });

  const { size, page, totalRows } = pagination;

  useEffect(() => {
    if (!enableSearch || !hasCompanyFiscalConfig) return;
    handleUpdateMissingDocuments();
  }, [enableSearch, hasCompanyFiscalConfig]); // eslint-disable-line react-hooks/exhaustive-deps

  return !availableYears ? (
    <Loading />
  ) : (
    <Box sx={{ margin: "12px 24px" }}>
      <MissingDocumentsFiltersBar
        allFiscalYears={allFiscalYears}
        countries={countries}
        utilityTypes={utilityTypes}
        sites={sites}
        businessUnits={businessUnits}
        countryRegions={countryRegions}
        companyMonths={companyMonths}
        currentFY={currentFY}
        setCurrentFY={setCurrentFY}
        currentCountries={currentCountries}
        currentSiteIds={currentSiteIds}
        currentUtilityTypeIds={currentUtilityTypeIds}
        currentBusinessUnits={currentBusinessUnits}
        currentCountryRegions={currentCountryRegions}
        currentStatus={currentStatus}
        allStatusOptions={allStatusOptions}
        areSitesFiltered={areSitesFiltered}
        getSitesFiltered={getSitesFiltered}
        countrySetSelectedValues={countrySetSelectedValues}
        countryOnChange={countryOnChange}
        utilityTypeSetSelectedValues={utilityTypeSetSelectedValues}
        utilityTypeOnChange={utilityTypeOnChange}
        sitesSetSelectedValues={sitesSetSelectedValues}
        sitesOnChange={sitesOnChange}
        businessUnitSetSelectedValues={businessUnitSetSelectedValues}
        businessUnitOnChange={businessUnitOnChange}
        regionSetSelectedValues={regionSetSelectedValues}
        regionOnChange={regionOnChange}
        statusSetSelectedValues={statusSetSelectedValues}
        statusOnChange={statusOnChange}
        enableSearch={enableSearch}
        searchActive={searchActive}
        onlyMissing={onlyMissing}
        setOnlyMissing={setOnlyMissing}
        setSearchActive={setSearchActive}
        resetFilterState={resetFilterState}
        defaultObject={defaultObject}
        handleUpdateMissingDocuments={handleUpdateMissingDocuments}
        setColumnFilters={setColumnFilters}
        resetSelection={resetSelection}
        resetPage={resetPage}
        size={size}
      />
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "32px",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          marginTop: "24px",
          fontSize: "12px",
          fontWeight: "500",
        }}
      >
        <DataBoxesList />
        <MissingDocumentsExportButton defaultObject={defaultObject} />
      </Box>
      <MissingDocumentsTable
        loadingMissingDocuments={loadingMissingDocuments}
        isTableRequestInProgress={isTableRequestInProgress}
        columns={columns}
        currentMissingData={currentMissingData}
        columnFilters={columnFilters}
        setColumnFilters={setColumnFilters}
        globalFilter={globalFilter}
        setGlobalFilter={setGlobalFilter}
        companyMonths={companyMonths}
        totalRows={totalRows}
        size={size}
        page={page}
        uiPage={uiPage}
        handlePageChange={handlePageChange}
        handleSizeChange={handleSizeChange}
        totalSelectedRows={totalSelectedRows}
        allRowsSelected={allRowsSelected}
        selectedRows={selectedRows}
        allCurrentCollectorsIds={allCurrentCollectorsIds}
        fromDate={defaultObject.current.fromDate}
        toDate={defaultObject.current.toDate}
        handleUpdateMissingDocuments={handleUpdateMissingDocuments}
        openMonthView={openMonthView}
        setOpenMonthView={setOpenMonthView}
        selectedMonthCell={selectedMonthCell}
        collectorMissingDocuments={collectorMissingDocuments}
        allMonths={allMonths}
      />
    </Box>
  );
};
